import { useQueryClient } from '@tanstack/react-query'
import { Form, message } from 'antd'
import { useNavigate } from 'react-router-dom'

import {
  useNotificationsServiceCreateTemplateEndpoint,
  useNotificationsServiceGetTemplatesListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  CreateTemplateRequest2,
  SystemLanguage
} from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { CreateNotificationCard } from '../components'

export const NotificationTemplatesCreatePage = () => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutate, isPending } = useNotificationsServiceCreateTemplateEndpoint({
    onSuccess: () => onMutationSuccess(),
    onError: (e) => onMutationError(e)
  })

  const onSubmit = async () => {
    await form.validateFields()

    const values = form.getFieldsValue(true)

    const requestBody: CreateTemplateRequest2 = {
      name: values?.name,
      eventId: values?.eventId,
      channelId: values?.channelId,
      destinationRoles: values?.destinationRoles,
      contents: [
        {
          languageId: SystemLanguage.RO,
          title: values?.title_ro,
          content: values?.content_ro
        },
        {
          languageId: SystemLanguage.RU,
          title: values?.title_ru,
          content: values?.content_ru
        }
      ]
    }

    mutate({ requestBody })
  }

  const onCancel = () => {
    form.resetFields()
    navigate(paths.notificationTemplates.root)
  }

  const onMutationSuccess = () => {
    navigate(paths.notificationTemplates.root)
    queryClient.invalidateQueries({
      queryKey: [useNotificationsServiceGetTemplatesListEndpointKey]
    })
  }

  const onMutationError = (e: unknown) => {
    if (e instanceof ApiError) {
      if ('contents[1]' in e.body.errors)
        message.error(t('notification_templates.error_ru'))
      else form?.setFields(formErrorHandler(e.body.errors))
    } else message.error(t('errors.something_went_wrong'))
  }

  return (
    <PageBreadcrumbs
      title={t('notification_templates.create_new_template')}
      items={[
        {
          title: t('menu.notification_templates'),
          path: paths.notificationTemplates.root
        },
        {
          title: t('notification_templates.create_new_template')
        }
      ]}
    >
      <CreateNotificationCard
        form={form}
        onSubmit={onSubmit}
        onCancel={onCancel}
        isPending={isPending}
      />
    </PageBreadcrumbs>
  )
}
